import * as XLSX from 'xlsx';
import { supabase } from './supabaseClient';

/**
 * Fetch complete provider data including emails and service rates
 * @returns {Promise<Object>} Object containing providers, serviceTypes, and emails
 */
export const fetchCompleteProviderData = async () => {
  try {
    // Fetch all service types (active and inactive)
    const { data: serviceTypesData, error: serviceTypesError } = await supabase
      .from('service_types')
      .select('*')
      .order('name');
    
    if (serviceTypesError) throw serviceTypesError;

    // Fetch all providers with service rates (active and inactive)
    const { data: providersData, error: providersError } = await supabase
      .from('providers')
      .select(`
        id,
        name,
        email,
        status,
        net_terms,
        default_hourly_rate,
        default_flat_rate,
        default_refusal_rate,
        provider_service_rates (
          id,
          service_type_id,
          rate_type,
          flat_rate,
          refusal_rate,
          hourly_rate,
          per_board,
          per_location,
          rate_1_unit,
          rate_2_units,
          rate_3_units,
          rate_4_units,
          rate_5_units,
          rate_6_units,
          rate_7_units,
          rate_8_units,
          rate_9_units,
          rate_10_units,
          service_types (
            id,
            name,
            code,
            rate_type
          )
        )
      `)
      .order('name');
    
    if (providersError) throw providersError;

    // Fetch emails for all providers
    const { data: authData } = await supabase.auth.getSession();
    const token = authData?.session?.access_token;

    if (!token) {
      throw new Error('No authentication token available');
    }

    // Fetch emails for each provider
    const providersWithEmails = await Promise.all(
      (providersData || []).map(async (provider) => {
        try {
          const response = await fetch(`/api/provider-emails/${provider.id}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          const data = await response.json();
          
          return {
            ...provider,
            emails: data.success ? data.emails : []
          };
        } catch (error) {
          console.error(`Error fetching emails for provider ${provider.id}:`, error);
          return {
            ...provider,
            emails: []
          };
        }
      })
    );

    return {
      providers: providersWithEmails,
      serviceTypes: serviceTypesData || [],
      error: null
    };
  } catch (error) {
    console.error('Error fetching complete provider data:', error);
    return {
      providers: [],
      serviceTypes: [],
      error
    };
  }
};

/**
 * Format currency value
 */
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = parseFloat(value);
  return isNaN(num) ? '' : num;
};

/**
 * Get configuration status for a provider
 */
const getConfigurationStatus = (provider, serviceTypes) => {
  const activeServiceTypes = serviceTypes.filter(st => st.is_active);
  const configuredCount = (provider.provider_service_rates || []).filter(rate => 
    activeServiceTypes.some(st => st.id === rate.service_type_id)
  ).length;
  
  if (configuredCount === 0) return 'Not Configured';
  if (configuredCount === activeServiceTypes.length) return 'Fully Configured';
  return 'Partially Configured';
};

/**
 * Get configured service types for a provider
 */
const getConfiguredServiceTypes = (provider, serviceTypes) => {
  const configuredIds = new Set(
    (provider.provider_service_rates || []).map(rate => rate.service_type_id)
  );
  
  return serviceTypes
    .filter(st => configuredIds.has(st.id))
    .map(st => st.name)
    .join(', ');
};

/**
 * Get unconfigured service types for a provider
 */
const getUnconfiguredServiceTypes = (provider, serviceTypes) => {
  const activeServiceTypes = serviceTypes.filter(st => st.is_active);
  const configuredIds = new Set(
    (provider.provider_service_rates || []).map(rate => rate.service_type_id)
  );
  
  return activeServiceTypes
    .filter(st => !configuredIds.has(st.id))
    .map(st => st.name)
    .join(', ');
};

/**
 * Generate overview sheet with all providers
 */
const generateOverviewSheet = (providers, serviceTypes) => {
  const headers = [
    'Provider Name',
    'Status',
    'Primary Email',
    'Additional Emails',
    'Net Terms',
    'Default Hourly Rate',
    'Default Flat Rate',
    'Default Refusal Rate',
    'Service Types Configured',
    'Service Types NOT Configured',
    'Configuration Status'
  ];

  const rows = providers.map(provider => {
    const primaryEmail = provider.emails.find(e => e.is_primary);
    const additionalEmails = provider.emails
      .filter(e => !e.is_primary)
      .map(e => `${e.email} (${e.email_type})`)
      .join(', ');

    return [
      provider.name,
      provider.status === 'active' ? 'Active' : 'Inactive',
      primaryEmail ? primaryEmail.email : (provider.email || 'No email'),
      additionalEmails || 'None',
      provider.net_terms ? `Net ${provider.net_terms}` : '',
      formatCurrency(provider.default_hourly_rate),
      formatCurrency(provider.default_flat_rate),
      formatCurrency(provider.default_refusal_rate),
      getConfiguredServiceTypes(provider, serviceTypes),
      getUnconfiguredServiceTypes(provider, serviceTypes),
      getConfigurationStatus(provider, serviceTypes)
    ];
  });

  return [headers, ...rows];
};

/**
 * Generate service type detail sheet
 */
const generateServiceTypeSheet = (serviceType, providers) => {
  const headers = [
    'Provider Name',
    'Status',
    'Rate Type'
  ];

  // Add rate-specific headers based on what we might encounter
  // We'll include all possible fields and leave empty if not applicable
  const rateHeaders = [
    'Flat Rate',
    'Hourly Rate',
    'Refusal Rate',
    'Per Board',
    'Per Location',
    'Rate 1 Unit',
    'Rate 2 Units',
    'Rate 3 Units',
    'Rate 4 Units',
    'Rate 5 Units',
    'Rate 6 Units',
    'Rate 7 Units',
    'Rate 8 Units',
    'Rate 9 Units',
    'Rate 10 Units'
  ];

  const allHeaders = [...headers, ...rateHeaders];

  const rows = providers.map(provider => {
    const serviceRate = (provider.provider_service_rates || []).find(
      rate => rate.service_type_id === serviceType.id
    );

    if (!serviceRate) {
      return [
        provider.name,
        provider.status === 'active' ? 'Active' : 'Inactive',
        'Not Configured',
        ...Array(rateHeaders.length).fill('')
      ];
    }

    return [
      provider.name,
      provider.status === 'active' ? 'Active' : 'Inactive',
      serviceRate.rate_type || 'Not Set',
      formatCurrency(serviceRate.flat_rate),
      formatCurrency(serviceRate.hourly_rate),
      formatCurrency(serviceRate.refusal_rate),
      formatCurrency(serviceRate.per_board),
      formatCurrency(serviceRate.per_location),
      formatCurrency(serviceRate.rate_1_unit),
      formatCurrency(serviceRate.rate_2_units),
      formatCurrency(serviceRate.rate_3_units),
      formatCurrency(serviceRate.rate_4_units),
      formatCurrency(serviceRate.rate_5_units),
      formatCurrency(serviceRate.rate_6_units),
      formatCurrency(serviceRate.rate_7_units),
      formatCurrency(serviceRate.rate_8_units),
      formatCurrency(serviceRate.rate_9_units),
      formatCurrency(serviceRate.rate_10_units)
    ];
  });

  return [allHeaders, ...rows];
};

/**
 * Apply professional styling to worksheet
 */
const applyExcelStyling = (worksheet, sheetData) => {
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  
  // Set column widths
  const colWidths = [];
  for (let C = range.s.c; C <= range.e.c; ++C) {
    let maxWidth = 10;
    for (let R = range.s.r; R <= range.e.r; ++R) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];
      if (cell && cell.v) {
        const cellLength = cell.v.toString().length;
        maxWidth = Math.max(maxWidth, Math.min(cellLength + 2, 50));
      }
    }
    colWidths.push({ wch: maxWidth });
  }
  worksheet['!cols'] = colWidths;

  // Freeze header row
  worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };

  // Style header row
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    if (!worksheet[cellAddress]) continue;
    
    worksheet[cellAddress].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1976D2" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };
  }

  // Apply currency formatting and styling to data rows
  for (let R = range.s.r + 1; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[cellAddress]) continue;

      const cell = worksheet[cellAddress];
      const headerCell = worksheet[XLSX.utils.encode_cell({ r: 0, c: C })];
      const headerValue = headerCell ? headerCell.v : '';

      // Apply currency formatting to rate columns
      if (typeof cell.v === 'number' && (
        headerValue.includes('Rate') || 
        headerValue.includes('Per Board') || 
        headerValue.includes('Per Location') ||
        headerValue.includes('Unit')
      )) {
        cell.t = 'n';
        cell.z = '$#,##0.00';
      }

      // Status column conditional formatting
      if (headerValue === 'Status') {
        const statusValue = cell.v;
        if (statusValue === 'Active') {
          cell.s = {
            fill: { fgColor: { rgb: "C8E6C9" } },
            font: { color: { rgb: "1B5E20" }, bold: true },
            alignment: { horizontal: "center" }
          };
        } else if (statusValue === 'Inactive') {
          cell.s = {
            fill: { fgColor: { rgb: "E0E0E0" } },
            font: { color: { rgb: "424242" } },
            alignment: { horizontal: "center" }
          };
        }
      }

      // Configuration Status column styling
      if (headerValue === 'Configuration Status') {
        const configValue = cell.v;
        if (configValue === 'Fully Configured') {
          cell.s = {
            fill: { fgColor: { rgb: "C8E6C9" } },
            font: { color: { rgb: "1B5E20" }, bold: true },
            alignment: { horizontal: "center" }
          };
        } else if (configValue === 'Partially Configured') {
          cell.s = {
            fill: { fgColor: { rgb: "FFF9C4" } },
            font: { color: { rgb: "F57F17" }, bold: true },
            alignment: { horizontal: "center" }
          };
        } else if (configValue === 'Not Configured') {
          cell.s = {
            fill: { fgColor: { rgb: "FFCDD2" } },
            font: { color: { rgb: "B71C1C" }, bold: true },
            alignment: { horizontal: "center" }
          };
        }
      }

      // Add borders to all cells
      if (!cell.s) cell.s = {};
      cell.s.border = {
        top: { style: "thin", color: { rgb: "CCCCCC" } },
        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
        left: { style: "thin", color: { rgb: "CCCCCC" } },
        right: { style: "thin", color: { rgb: "CCCCCC" } }
      };
    }
  }

  return worksheet;
};

/**
 * Export all providers to Excel
 * @param {Array} providers - Array of provider objects with emails and service rates
 * @param {Array} serviceTypes - Array of service type objects
 * @returns {void} Triggers download of Excel file
 */
export const exportProvidersToExcel = (providers, serviceTypes) => {
  try {
    const workbook = XLSX.utils.book_new();

    // Generate Overview sheet
    const overviewData = generateOverviewSheet(providers, serviceTypes);
    const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
    applyExcelStyling(overviewSheet, overviewData);
    XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Overview');

    // Generate a sheet for each active service type
    const activeServiceTypes = serviceTypes.filter(st => st.is_active);
    activeServiceTypes.forEach(serviceType => {
      const serviceTypeData = generateServiceTypeSheet(serviceType, providers);
      const serviceTypeSheet = XLSX.utils.aoa_to_sheet(serviceTypeData);
      applyExcelStyling(serviceTypeSheet, serviceTypeData);
      
      // Sanitize sheet name (Excel has 31 char limit and no special chars)
      let sheetName = serviceType.name.substring(0, 31);
      sheetName = sheetName.replace(/[:\\/?*\[\]]/g, '-');
      
      XLSX.utils.book_append_sheet(workbook, serviceTypeSheet, sheetName);
    });

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Provider_Rate_Table_${timestamp}.xlsx`;

    // Write and download
    XLSX.writeFile(workbook, filename);

    return { success: true, filename };
  } catch (error) {
    console.error('Error exporting providers to Excel:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export single provider to Excel
 * @param {Object} provider - Provider object with emails and service rates
 * @param {Array} serviceTypes - Array of service type objects
 * @returns {void} Triggers download of Excel file
 */
export const exportSingleProviderToExcel = (provider, serviceTypes) => {
  try {
    const workbook = XLSX.utils.book_new();

    // Sheet 1: Provider Overview
    const primaryEmail = provider.emails?.find(e => e.is_primary);
    const additionalEmails = provider.emails?.filter(e => !e.is_primary) || [];

    const overviewData = [
      ['Provider Information', ''],
      ['Provider Name', provider.name],
      ['Status', provider.status === 'active' ? 'Active' : 'Inactive'],
      ['Net Terms', provider.net_terms ? `Net ${provider.net_terms}` : ''],
      [''],
      ['Email Addresses', ''],
      ['Primary Email', primaryEmail ? primaryEmail.email : (provider.email || 'No email')],
      ...additionalEmails.map(email => [
        `${email.email_type} Email`,
        email.email
      ]),
      [''],
      ['Default Rates', ''],
      ['Default Hourly Rate', formatCurrency(provider.default_hourly_rate)],
      ['Default Flat Rate', formatCurrency(provider.default_flat_rate)],
      ['Default Refusal Rate', formatCurrency(provider.default_refusal_rate)],
      [''],
      ['Configuration', ''],
      ['Service Types Configured', getConfiguredServiceTypes(provider, serviceTypes)],
      ['Service Types NOT Configured', getUnconfiguredServiceTypes(provider, serviceTypes)],
      ['Configuration Status', getConfigurationStatus(provider, serviceTypes)]
    ];

    const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
    
    // Style overview sheet
    overviewSheet['!cols'] = [{ wch: 30 }, { wch: 50 }];
    
    XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Provider Overview');

    // Sheet 2: Service Rates Detail
    const ratesHeaders = [
      'Service Type',
      'Status',
      'Rate Type',
      'Flat Rate',
      'Hourly Rate',
      'Refusal Rate',
      'Per Board',
      'Per Location',
      'Rate 1 Unit',
      'Rate 2 Units',
      'Rate 3 Units',
      'Rate 4 Units',
      'Rate 5 Units',
      'Rate 6 Units',
      'Rate 7 Units',
      'Rate 8 Units',
      'Rate 9 Units',
      'Rate 10 Units'
    ];

    const ratesRows = serviceTypes.map(serviceType => {
      const serviceRate = (provider.provider_service_rates || []).find(
        rate => rate.service_type_id === serviceType.id
      );

      if (!serviceRate) {
        return [
          serviceType.name,
          serviceType.is_active ? 'Active' : 'Inactive',
          'Not Configured',
          ...Array(ratesHeaders.length - 3).fill('')
        ];
      }

      return [
        serviceType.name,
        serviceType.is_active ? 'Active' : 'Inactive',
        serviceRate.rate_type || 'Not Set',
        formatCurrency(serviceRate.flat_rate),
        formatCurrency(serviceRate.hourly_rate),
        formatCurrency(serviceRate.refusal_rate),
        formatCurrency(serviceRate.per_board),
        formatCurrency(serviceRate.per_location),
        formatCurrency(serviceRate.rate_1_unit),
        formatCurrency(serviceRate.rate_2_units),
        formatCurrency(serviceRate.rate_3_units),
        formatCurrency(serviceRate.rate_4_units),
        formatCurrency(serviceRate.rate_5_units),
        formatCurrency(serviceRate.rate_6_units),
        formatCurrency(serviceRate.rate_7_units),
        formatCurrency(serviceRate.rate_8_units),
        formatCurrency(serviceRate.rate_9_units),
        formatCurrency(serviceRate.rate_10_units)
      ];
    });

    const ratesData = [ratesHeaders, ...ratesRows];
    const ratesSheet = XLSX.utils.aoa_to_sheet(ratesData);
    applyExcelStyling(ratesSheet, ratesData);
    XLSX.utils.book_append_sheet(workbook, ratesSheet, 'Service Rates');

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const sanitizedName = provider.name.replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `${sanitizedName}_Rates_${timestamp}.xlsx`;

    // Write and download
    XLSX.writeFile(workbook, filename);

    return { success: true, filename };
  } catch (error) {
    console.error('Error exporting single provider to Excel:', error);
    return { success: false, error: error.message };
  }
};

